import { Button, Tooltip, useNotifications } from '@ph-react-ui/core';
import { format } from 'date-fns';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/auth/useAuth';
import { useGetMonthlyMenuByStudent } from '../../hooks/payments/useGetMonthlyMenuByStudent';
import { useSendFoodParameters } from '../../hooks/payments/useSendFoodParameters';
import { useGetAuthenticatedParentProfile } from '../../hooks/profile/parent/useGetAuthenticatedParentProfile';
import { useGetStudentsForParent } from '../../hooks/users/parents/useGetStudentsForParent';
import type { BoricaParametersDto } from '../../models/payments/borica-parameters.dto';
import type { FoodParametersDto } from '../../models/payments/food-parameters.dto';
import { SCHOOL_YEAR_START_MONTH } from '../../utils/constants/food-management/schoolYearStartMonth';
import { DEFAULT_PAYMENT_FORM_STATE } from '../../utils/constants/payments/default-payment-form-state';
import {
  isPaymentSystemLocked,
  PaymentLockdownBanner,
} from '../../utils/constants/payments/payment-lockdown';
import { displayPrice } from '../../utils/displayPrice';
import type { Currency } from '../../utils/enums/currency-type.enum';
import { ScopeAccess } from '../../utils/enums/scope-access.enum';
import { FormSkeleton } from '../shared/components/skeletons/FormSkeleton';
import { BoricaResponseForm } from './BoricaResponseForm';

export function FoodPayment() {
  const { authState } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const notification = useNotifications();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [formState, setFormState] = useState<BoricaParametersDto>(
    DEFAULT_PAYMENT_FORM_STATE
  );
  const params = useParams<{
    studentId: string;
    month: string;
    year: string;
  }>();
  const location = useLocation();
  const isLocked = useMemo(() => isPaymentSystemLocked(), []);

  const studentId = params.studentId ?? '';
  const monthParam = params.month ?? '';
  const yearParam = params.year ?? '';

  const { mealMenuIds, paymentItems } = useMemo(() => {
    const state = location.state as {
      mealMenuIds?: string[];
      paymentItems?: Array<{
        mealMenuId: string;
        month: number;
        year: number;
        amount: number;
        currency: Currency;
      }>;
    };
    return {
      mealMenuIds: state?.mealMenuIds ?? [],
      paymentItems: state?.paymentItems ?? [],
    };
  }, [location.state]);

  const getCurrentSchoolYearStart = useCallback(() => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    return currentMonth >= SCHOOL_YEAR_START_MONTH
      ? currentYear
      : currentYear - 1;
  }, []);

  const getSchoolYearFromMonthYear = useCallback(
    (month: string, year: string) => {
      if (!month || !year) return getCurrentSchoolYearStart();

      const monthNum = Number(month);
      const yearNum = Number(year);

      return monthNum >= SCHOOL_YEAR_START_MONTH ? yearNum : yearNum - 1;
    },
    [getCurrentSchoolYearStart]
  );

  const selectedStartYear = useMemo(
    () =>
      monthParam && yearParam
        ? getSchoolYearFromMonthYear(monthParam, yearParam)
        : getCurrentSchoolYearStart(),
    [
      monthParam,
      yearParam,
      getSchoolYearFromMonthYear,
      getCurrentSchoolYearStart,
    ]
  );

  const computedSchoolYear = useMemo(
    () => `${selectedStartYear}-${selectedStartYear + 1}`,
    [selectedStartYear]
  );

  const { data: monthlyMenuItems, isLoading: isLoadingMonthlyMenu } =
    useGetMonthlyMenuByStudent(studentId, selectedStartYear);

  const { data: parentProfile } = useGetAuthenticatedParentProfile();
  const { data: students, isLoading: isLoadingStudents } =
    useGetStudentsForParent({
      enabled: Boolean(parentProfile),
      excludeArchived: true,
      isStudyingInCurrentYear: true,
    });

  const mainParent = useMemo(
    () => parentProfile?.parents.find((parent) => parent.isPrimaryParent),
    [parentProfile]
  );

  const selectedStudent = useMemo(
    () => students?.find((s) => s.id === studentId),
    [students, studentId]
  );

  const selectedMonthlyItems = useMemo(() => {
    return paymentItems.sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });
  }, [paymentItems]);

  const totalMonthlyAmount = useMemo(
    () =>
      selectedMonthlyItems.reduce((sum: number, item) => sum + item.amount, 0),
    [selectedMonthlyItems]
  );

  const canPaySelectedMonth = useMemo(() => {
    if (!monthlyMenuItems || !selectedMonthlyItems.length) return true;

    const unpaidItems = monthlyMenuItems
      .filter((item) => !item.paidAt)
      .sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      });

    if (unpaidItems.length === 0) return true;

    const firstUnpaidDate = new Date(
      unpaidItems[0].year,
      unpaidItems[0].month - 1
    );
    const selectedDate = new Date(
      selectedMonthlyItems[0].year,
      selectedMonthlyItems[0].month - 1
    );

    return selectedDate.getTime() === firstUnpaidDate.getTime();
  }, [monthlyMenuItems, selectedMonthlyItems]);

  const isLoading = isLoadingMonthlyMenu || isLoadingStudents;

  const methods = useForm<FoodParametersDto>({
    defaultValues: {
      studentId: '',
      mealMenuId: [],
    },
  });

  const sendFoodParameters = useSendFoodParameters();
  const isSendFoodParametersPending = useMemo(
    () => sendFoodParameters.state?.[0]?.status === 'pending',
    [sendFoodParameters]
  );

  const onNavigateBack = useCallback(() => navigate(-1), [navigate]);

  useEffect(() => {
    if (formRef.current && formState.saleRequest.AMOUNT) {
      formRef.current?.submit();
    }
  }, [formState]);

  const onPayNow = useCallback(
    async (data: FoodParametersDto) => {
      try {
        const res = await sendFoodParameters.mutation.mutateAsync(data);
        setFormState(res);
      } catch (error: any) {
        notification.danger(t(`NETWORK_ERRORS.${error.errors[0]}`));
      }
    },
    [sendFoodParameters.mutation, notification, t]
  );

  const handlePayPerStudent = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const paymentData: FoodParametersDto = {
        studentId: (event.target as HTMLButtonElement).id,
        mealMenuId: mealMenuIds,
      };
      methods.handleSubmit(() => onPayNow(paymentData))();
    },
    [mealMenuIds, methods, onPayNow]
  );

  const renderMonthlyMenuPayment = () => {
    if (!selectedStudent || !selectedMonthlyItems.length) {
      return (
        <div
          className="payments__pre-payment__student-section"
          style={{ textAlign: 'center', padding: '2rem' }}
        >
          <p className="payments__pre-payment__value">
            {t('FOOD_MANAGEMENT.MESSAGES.NO_ITEMS_SELECTED')}
          </p>
        </div>
      );
    }

    return (
      <div>
        <div className="payments__pre-payment__student-section">
          <div className="payments__pre-payment__two-column-layout">
            <div className="payments__pre-payment__student-info">
              <p className="payments__pre-payment__label">
                {t('USERS.TABLE.ROLE.STUDENT')}
              </p>
              <p className="payments__pre-payment__value">
                {selectedStudent.name}
              </p>
            </div>
            <div className="payments__pre-payment__student-info payments__pre-payment__student-info--divider">
              <p className="payments__pre-payment__label">
                {t('USERS.TABLE.ROLE.PARENT')}
              </p>
              <p className="payments__pre-payment__value">{mainParent?.name}</p>
            </div>
          </div>
        </div>

        <h4 className="payments__pre-payment__section-title">
          {t('PAYMENTS.TITLES.MONTHLY_MENU')}
        </h4>

        {selectedMonthlyItems.map((item, index) => {
          const itemDate = new Date(item.year, item.month - 1);
          const monthName = t(
            `MONTHS.${format(itemDate, 'MMMM').toUpperCase()}`
          );

          return (
            <div key={index} className="payments__pre-payment__installment">
              <div className="payments__pre-payment__installment-grid">
                <div className="payments__pre-payment__installment-item payments__pre-payment__installment-item--merged">
                  <p className="payments__pre-payment__label">
                    {t('PAYMENTS.TABLE.HEADERS.MONTH')}
                  </p>
                  <p className="payments__pre-payment__value">{monthName}</p>
                </div>

                <div className="payments__pre-payment__installment-item">
                  <p className="payments__pre-payment__label">
                    {t('TAXES.TABLE.HEADERS.SCHOOL_YEAR')}
                  </p>
                  <p className="payments__pre-payment__value">
                    {computedSchoolYear}
                  </p>
                </div>

                <div className="payments__pre-payment__installment-item">
                  <p className="payments__pre-payment__label">&nbsp;</p>
                  <p className="payments__pre-payment__value">&nbsp;</p>
                </div>

                <div className="payments__pre-payment__installment-item payments__pre-payment__installment-item--amount">
                  <p className="payments__pre-payment__label">
                    {t('TAXES.TABLE.HEADERS.AMOUNT')}
                  </p>
                  <p className="payments__pre-payment__value">
                    {displayPrice(
                      item.amount,
                      t('PAYMENTS.LABELS.BGN'),
                      t('PAYMENTS.LABELS.EUR'),
                      item.currency
                    )}
                  </p>
                </div>
              </div>
            </div>
          );
        })}

        <div className="payments__pre-payment__total-section">
          <div className="payments__pre-payment__total-row">
            <span className="payments__pre-payment__total-label">
              {t('PAYMENTS.TITLES.TOTAL')}
            </span>
            <span className="payments__pre-payment__total-value">
              {displayPrice(
                totalMonthlyAmount,
                t('PAYMENTS.LABELS.BGN'),
                t('PAYMENTS.LABELS.EUR'),
                selectedMonthlyItems[0]?.currency
              )}
            </span>
          </div>
        </div>

        {authState?.scope?.includes(ScopeAccess.MakePayments) && (
          <div className="payments__pre-payment__actions">
            <Button variant="outlined" onClick={onNavigateBack}>
              {t('ACTIONS.BACK')}
            </Button>
            {!canPaySelectedMonth ? (
              <Tooltip
                position="top"
                message={t('PAYMENTS.TABLE.LABELS.PAY_PREVIOUS_FIRST')}
              >
                <Button
                  type="submit"
                  id={selectedStudent.id}
                  onClick={handlePayPerStudent}
                  loading={isSendFoodParametersPending}
                  disabled
                >
                  {t('ACTIONS.PAY_NOW')}
                </Button>
              </Tooltip>
            ) : (
              <Button
                type="submit"
                id={selectedStudent.id}
                onClick={handlePayPerStudent}
                loading={isSendFoodParametersPending}
                disabled={isLocked}
              >
                {t('ACTIONS.PAY_NOW')}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="payments__pre-payment">
      {isLoading && <FormSkeleton />}
      {!isLoading && (
        <FormProvider {...methods}>
          <form>
            <h3 className="payments__pre-payment__title">
              {t('PAYMENTS.TITLES.FOOD_ORDER')}
            </h3>
            <PaymentLockdownBanner />
            {renderMonthlyMenuPayment()}
          </form>
        </FormProvider>
      )}
      <BoricaResponseForm ref={formRef} formState={formState} />
    </div>
  );
}
